<template>
  <div>
    <v-container class="mb-16" fluid :class="isMobile() ? 'pb-10' : ''">
      <TimePairFilterBar :manager="false" :timePairs="timePairs"/>

      <PayCycleTable
        :timePairs="currentPayCycleTimePairs"
        :header="currentPayCycleHeader"
        :timePairsLoading="timePairsLoading"
        actionable
      />
      <PayCycleTable
        :timePairs="previousPayCycleTimePairs"
        :header="previousPayCycleHeader"
        :timePairsLoading="timePairsLoading"
        :actionable="false"
        :class="(isMenuDrawerClosable() && !isMobile()) ? 'mb-6' : ''"
      />
    </v-container>

    <v-bottom-navigation
      fixed
      dark
      background-color="primary"
      :class="isMenuDrawerClosable() ? 'mb-12' : ''"
    >
      <v-card v-if="!isMenuDrawerClosable()" flat disabled width="256px" color="secondary"/>
      <v-btn text :to="{ name: 'Clock' }">
        <small
          v-if="isConcurrentEmploymentActive && isMobile() && activeTimePairId"
          class="pl-2 text-truncate"
          style="max-width: 160px"
        >
          {{ activeAssignmentDescription }}
        </small>
        <span class="white--text headline">{{ formattedTimer }}</span>
      </v-btn>
      <v-btn
        v-if="isConcurrentEmploymentActive && !isMobile() && activeTimePairId"
        text
        class="mx-8"
        disabled
      >
        <span
          class="white--text subtitle-1 text-truncate"
          style="max-width: 260px;"
        >
          {{ activeAssignmentDescription }}
        </span>
      </v-btn>
      <v-spacer/>
      <v-divider vertical/>
      <v-btn
        v-if="isTimePairAddFeatureActive && canCaptureTime"
        :x-large="!isMobile()"
        @click="timePairAddDialog = true"
      >
        <span>{{ $t('actions.addTime') }}</span>
        <v-icon>alarm_add</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <TimePairDialog
      :value="timePairAddDialog"
      @close="timePairAddDialog = false"
    />
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';

import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';

import filter from 'lodash/filter';

import constants from '@/constants';

import DateTimeUtil from '@/util/date-time-util';

import displayAdaptable from '@/mixins/displayAdaptable';
import resolveMobileBackButton from '@/mixins/resolveMobileBackButton';

import PayCycleTable from '@/components/timepairs/PayCycleTable.vue';
import TimePairFilterBar from '@/components/timepairs/TimePairFilterBar.vue';
import TimePairDialog from '@/components/timepairs/TimePairDialog.vue';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import AuthorisationUtil from '@/util/authorisation-util';
import hasAssignmentFeatures from '@/mixins/clocking/hasAssignmentFeatures';

export default {
  name: 'EmployeeTimePairs',

  components: {
    TimePairDialog,
    TimePairFilterBar,
    PayCycleTable,
  },

  mixins: [
    displayAdaptable,
    hasTimePairFeatures,
    resolveMobileBackButton,
    hasAssignmentFeatures,
  ],

  data() {
    return {
      timePairsLoading: false,
      timePairAddDialog: false,
    };
  },

  computed: {
    ...mapState({
      activeAssignmentDescription: (state) => state.activeTimePair.employeeAssignment.position.description,
      activeTimePairId: (state) => state.activeTimePair.id,
      timePairs: (state) => state.timePairs,
      timerDuration: (state) => state.timer.duration,
    }),
    ...mapGetters(['getFilteredTimes']),

    currentPayCycleHeader() {
      return this.isMobile() ? this.$t('tableHeaders.current') : this.$t('tableHeaders.currentPayCycle');
    },

    previousPayCycleHeader() {
      return this.isMobile() ? this.$t('tableHeaders.prev') : this.$t('tableHeaders.prevPayCycle');
    },

    currentPayCycleTimePairs() {
      return filter(
        this.getFilteredTimes(this.timePairs),
        (timePair) => new Date(timePair.startDate) >= DateTimeUtil.getStartOfCurrentPayCycle(),
      );
    },

    previousPayCycleTimePairs() {
      return filter(
        this.getFilteredTimes(this.timePairs),
        (timePair) => new Date(timePair.startDate) < DateTimeUtil.getStartOfCurrentPayCycle(),
      );
    },

    formattedTimer() {
      if (this.timerDuration === constants.DefaultState().timer.duration) {
        return '--:--:--';
      }

      return this.timerDuration;
    },

    canCaptureTime() {
      return AuthorisationUtil.employeeHasCaptureTimeAuthority();
    },
  },

  watch: {
    $route() {
      this.loadWorkSchedules().finally(() => {
        this.retrieveEmployeeTimePairs();
      });
    },
  },

  methods: {
    ...mapMutations(['setPageTitle']),
    ...mapActions(['loadEmployeeTimePairs', 'loadWorkSchedules']),

    retrieveEmployeeTimePairs() {
      this.timePairsLoading = true;

      const startDate = format(DateTimeUtil.getStartOfPreviousPayCycle(),
        DateTimeUtil.constants.isoFormat);
      const endDate = format(endOfDay(new Date()), DateTimeUtil.constants.isoFormat);

      this.loadEmployeeTimePairs({ startDate, endDate })
      .then(() => {
        this.timePairsLoading = false;
      });
    },
  },

  created() {
    this.setPageTitle(this.$t('titles.clockingHistory'));
    this.loadWorkSchedules().finally(() => {
      this.retrieveEmployeeTimePairs();
    });
  },
};
</script>

<style scoped>
</style>
